/**
 * Home (section 6) as a dashboard: what needs me and how the brand is doing, in one screen. The
 * page's own range and the "Sample data" chip above; the setup guide first while it is unfinished;
 * the KPI strip; the trend chart beside what needs attention and right now; the most played beside
 * money (or devices, for a brand that does not sell); recent activity beside the changes not live,
 * storage and the finished setup guide.
 *
 * This file only composes: where each card sits is src/home/dashboard-grid.tsx, the setup guide's
 * rules src/home/use-setup-guide.ts, what needs attention src/home/attention.ts, the analytics
 * reads and cards src/analytics/home.
 */

import Button from "@web-ui/components/Button";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import { DevicesCard } from "@/analytics/home/devices-card";
import { HomeTrend } from "@/analytics/home/home-trend";
import { KpiStrip } from "@/analytics/home/kpi-strip";
import { MoneySummary } from "@/analytics/home/money-summary";
import { RightNow } from "@/analytics/home/right-now";
import { TopContentCard } from "@/analytics/home/top-content-card";
import { useHomeAnalytics } from "@/analytics/home/use-home-analytics";
import { parseRange } from "@/analytics/range";
import { useReportScope } from "@/analytics/scope";
import { PageBar } from "@/components/page-bar";
import { attentionList } from "@/home/attention";
import { ChangesNotLive } from "@/home/changes-not-live";
import { DashboardGrid } from "@/home/dashboard-grid";
import { HomeToolbar } from "@/home/home-toolbar";
import { NeedsAttention } from "@/home/needs-attention";
import { RecentActivity } from "@/home/recent-activity";
import { SetupGuide } from "@/home/setup-guide";
import { StorageCard } from "@/home/storage-card";
import { useHomeData } from "@/home/use-home-data";
import { useSetupGuide } from "@/home/use-setup-guide";
import { go, href, type Route } from "@/lib/route";
import { showsLive } from "@/shell/navigation";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";

export function Home({ route }: { route: Route }) {
  const studio = useStudio();
  const { client, pack, changes, counts, brandView, canEdit, labelOf, items, products } = studio;
  const brand = client.brand.id;
  const days = parseRange(route.range);
  const link = (r: Omit<Route, "brand">) => href({ brand, ...r } as Route);
  const data = useHomeData(client);
  const guide = useSetupGuide(studio, data);
  const sells = products === null ? null : products.length > 0;
  const scope = useReportScope(days);
  const stats = useHomeAnalytics(scope, sells);
  const pending = changes?.changes.length ?? 0;
  const nameOf = (id: string) => items?.find((e) => e.id === id)?.label || undefined;
  const attention = data ? attentionList({ brand, pack, data, errors: guide.otherErrors, nameOf }) : [];
  const setupGuide = guide.guideShows ? <SetupGuide key={String(guide.setupDone)} steps={guide.steps} onDismiss={guide.dismiss} /> : null;
  const firstStep = changes && !changes.live ? { label: "Review & publish", href: link({ section: "review" }) } : undefined;

  return (
    <>
      <PageBar lead={<HomeToolbar days={days} onRange={(next) => go({ ...route, range: next })} sample={stats.sample} />}>
        {guide.dismissed && guide.studioReady && !guide.setupDone ? <Button type="button" variant="ghost" size="sm" onClick={guide.show}>Show setup guide</Button> : null}
        {pending === 0 && canEdit ? <Button as="a" variant="primary" icon="plus" href={link({ section: "library", type: "title", show: "new" })}>{`New ${lower(noun(pack, "title"))}`}</Button> : null}
      </PageBar>

      <DashboardGrid
        guide={guide.setupDone ? null : setupGuide}
        attention={guide.topReady ? <NeedsAttention attention={attention} stepsWaiting={guide.stepProblems.size > 0} /> : <SkeletonCard label="Checking what needs you" rows={1} />}
        attentionQuiet={!guide.topReady || attention.length === 0}
        kpis={<KpiStrip kpis={stats.kpis} realtime={stats.realtime} pack={pack} />}
        trend={<HomeTrend scope={scope} kpis={stats.kpis} firstStep={firstStep} />}
        rightNow={<RightNow report={stats.realtime} pack={pack} nameOf={scope.nameOf} showLive={showsLive(brandView?.manifest.features, counts)} />}
        top={<TopContentCard report={stats.content} nameOf={scope.nameOf} noun={noun(pack, "title", "many")} contentHref={link({ section: "analytics", report: "content", range: days })} />}
        money={sells === null ? <SkeletonCard label="Loading" rows={7} />
          : sells ? <MoneySummary report={stats.money} pack={pack} moneyHref={link({ section: "analytics", report: "money", range: days })} />
          : <DevicesCard report={stats.audience} audienceHref={link({ section: "analytics", report: "audience", range: days })} />}
        changes={<ChangesNotLive changes={changes} reviewHref={link({ section: "review" })} />}
        activity={<RecentActivity audit={data?.audit ?? null} pack={pack} labelOf={labelOf} historyHref={link({ section: "settings", page: "history" })} />}
        storage={<StorageCard budget={data?.budget ?? null} loading={!data} filesHref={link({ section: "settings", page: "files" })} />}
        foldedGuide={guide.setupDone ? setupGuide : null}
      />
    </>
  );
}
